import React, { useState, useEffect } from 'react';
import { PhotoManager } from './PhotoManager';
import { BioEditor } from './BioEditor';
import { InterestSelector } from './InterestSelector';
import { AboutEditor } from './AboutEditor';
import { ProfileEditorData, ProfilePhoto } from './types';
import {
  EditorContainer,
  EditorHeader,
  EditorTitle,
  EditorNavigation,
  EditorTab,
  EditorContent,
  EditorActions,
  SaveButton,
  CancelButton,
  EditorStatus,
  StatusMessage
} from '../../styles/Profile/ProfileEditor.styles';

interface ProfileEditorProps {
  initialData: ProfileEditorData;
  onSave: (data: ProfileEditorData) => void;
  onCancel: () => void;
  isPremium?: boolean;
  onPremium?: () => void;
  userId: string;
}

const EDITOR_TABS = [
  { id: 'photos', label: 'Media' },
  { id: 'about', label: 'About' },
  { id: 'bio', label: 'Bio' },
  { id: 'interests', label: 'Interests' }
  // 'Settings' moved to /settings, reached from the profile menu. Editing your
  // profile is about what other people see; a theme is about what you see, and
  // nobody looking for one goes hunting through their photos and bio to find it.
];

export const ProfileEditor: React.FC<ProfileEditorProps> = ({
  initialData,
  onSave,
  onCancel,
  isPremium = false,
  onPremium,
  userId
}) => {
  const [activeTab, setActiveTab] = useState('photos');
  const [profileData, setProfileData] = useState<ProfileEditorData>(initialData);
  const [hasChanges, setHasChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleDataChange = (section: keyof ProfileEditorData, data: any) => {
    setProfileData(prev => ({
      ...prev,
      [section]: data,
      lastUpdated: new Date(),
      version: prev.version + 1
    }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(profileData);
      setHasChanges(false);
      setSaveStatus('success');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } catch (error) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus('idle'), 3000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (hasChanges) {
      const confirmed = window.confirm(
        'You have unsaved changes. Are you sure you want to cancel?'
      );
      if (!confirmed) return;
    }
    onCancel();
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'photos':
        return (
          <PhotoManager
            photos={profileData.photos}
            onChange={(photos) => handleDataChange('photos', photos)}
            isPremium={isPremium}
            onPremium={onPremium}
            userId={userId}
          />
        );
      case 'about':
        return (
          <AboutEditor
            basicInfo={profileData.basicInfo}
            onChange={(basicInfo) => handleDataChange('basicInfo', basicInfo)}
          />
        );
      case 'bio':
        return (
          <BioEditor
            bio={profileData.bio}
            onChange={(bio) => handleDataChange('bio', bio)}
          />
        );
      case 'interests':
        return (
          <InterestSelector
            interests={profileData.interests}
            onChange={(interests) => handleDataChange('interests', interests)}
          />
        );
      default:
        return null;
    }
  };

  return (
    <EditorContainer>
      <EditorHeader>
        <EditorTitle>Edit Profile</EditorTitle>
        <EditorNavigation>
          {EDITOR_TABS.map(tab => (
            <EditorTab
              key={tab.id}
              $isActive={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </EditorTab>
          ))}
        </EditorNavigation>
      </EditorHeader>

      <EditorContent>
        {renderTabContent()}
      </EditorContent>

      <EditorActions>
        <div>
          <CancelButton onClick={handleCancel}>
            Cancel
          </CancelButton>
        </div>

        <div>
          <SaveButton
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </SaveButton>
        </div>
      </EditorActions>

      <EditorStatus>
        {/* No success message here. The page shows a toast for that, and two
            confirmations of one click is just noise. Failure still speaks up
            below, because that is the case nobody should be able to miss. */}
        {saveStatus === 'error' && (
          <StatusMessage $type="error">
            ✗ Failed to save profile. Please try again.
          </StatusMessage>
        )}
        {hasChanges && saveStatus === 'idle' && (
          <StatusMessage $type="warning">
            You have unsaved changes
          </StatusMessage>
        )}
      </EditorStatus>
    </EditorContainer>
  );
};
